import { useLazyQuery, useMutation } from '@apollo/client'
import {
  Button,
  CircularProgress,
  Collapse,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import FilterListIcon from '@material-ui/icons/FilterList'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import SectionTitle from '../../components/SectionTitle'
import {
  ACCOMMODATION_BOOKING_PERMISSION_QUERY,
  UPDATE_ACCOMMODATION_BOOKING_PERMISSION_MUTATION
} from '../../graphql/accommodationBookingPermission'
import { getTranslate } from '../../localization'
import {
  BookingPermission,
  BookingPermissionAccommodationInput,
  UpdateBookingPermission
} from '../../types/bookingPermission'
import BookingPermissionFilters from './BookingPermissionFilters'
import BookingPermissionTable from './BookingPermissionTable'
import useStyles from './styles/index'

function AccommodationBookingPermission() {
  const classes = useStyles()
  const theme = useTheme()
  const params = useParams<{ accID: string }>()

  const [filterButtonSelected, setFilterButtonSelected] =
    useState<boolean>(true)
  const [editMode, setEditMode] = useState<boolean>(false)
  const [input, setInput] = useState<BookingPermissionAccommodationInput>({
    accommodationId: params.accID,
    channelType: undefined,
    actionType: undefined
  })
  const [data, setData] = useState<BookingPermission[]>([])

  const [
    getBookingPermissions,
    {
      data: bookingPermissionsData,
      loading: loadingForBookingPermissions,
      refetch
    }
  ] = useLazyQuery<{
    accommodationBookingPermission: BookingPermission[]
  }>(ACCOMMODATION_BOOKING_PERMISSION_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (resp) => {
      setData(resp.accommodationBookingPermission)
    }
  })

  const [
    updateBookingPermission,
    { loading: loadingForUpdateBookingPermission }
  ] = useMutation(UPDATE_ACCOMMODATION_BOOKING_PERMISSION_MUTATION, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (resp) => {
      setEditMode(false)
      refetch()
      toast.success(resp.updateAccommodationBookingPermission.message)
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  const onUpdateClick = () => {
    const temp: UpdateBookingPermission[] = []
    data.forEach((ele) => {
      temp.push({
        accommodationId: ele.accommodationId,
        channelType: ele.channelType,
        actionType: ele.actionType,
        allowOperation: ele.allowOperation,
        allowDiscount: ele.allowDiscount,
        reservationType: ele.reservationType
      })
    })

    updateBookingPermission({
      variables: {
        input: temp
      }
    })
  }

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <div>
      <div className={classes.header}>
        <SectionTitle title={getTranslate('Booking Permission')} marginBottom />
        <Button
          size={isSmallScreen ? 'large' : 'medium'}
          startIcon={<EditIcon />}
          classes={{
            root: classes.editButton,
            startIcon: isSmallScreen ? classes.startIcon : undefined
          }}
          variant="outlined"
          color="primary"
          onClick={() => {
            setEditMode(!editMode)
            setData(
              bookingPermissionsData?.accommodationBookingPermission
                ? bookingPermissionsData.accommodationBookingPermission
                : []
            )
          }}>
          {isSmallScreen ? '' : getTranslate(editMode ? 'Cancel Edit' : 'Edit')}
        </Button>
        <Button
          size={isSmallScreen ? 'large' : 'medium'}
          startIcon={<FilterListIcon />}
          classes={{
            startIcon: isSmallScreen ? classes.startIcon : undefined
          }}
          variant="contained"
          color="primary"
          onClick={() => setFilterButtonSelected(!filterButtonSelected)}>
          {isSmallScreen ? '' : getTranslate('Filter')}
        </Button>
      </div>
      <Collapse in={filterButtonSelected}>
        <BookingPermissionFilters
          input={input}
          setInput={setInput}
          getBookingPermissions={getBookingPermissions}
          loadingForBookingPermissions={loadingForBookingPermissions}
        />
      </Collapse>
      <BookingPermissionTable
        data={data}
        setData={setData}
        loadingForBookingPermissions={loadingForBookingPermissions}
        editMode={editMode}
        refetch={refetch}
      />
      <div className={classes.footer}>
        <Button
          size={'medium'}
          variant="contained"
          color="primary"
          disabled={!editMode || loadingForUpdateBookingPermission}
          onClick={() => onUpdateClick()}>
          {getTranslate('Update')}
          {loadingForUpdateBookingPermission && (
            <CircularProgress
              className={classes.circularProgress}
              size={16}
              color="primary"
            />
          )}
        </Button>
      </div>
    </div>
  )
}

export default AccommodationBookingPermission
